import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Presentation, Plus, Copy, RefreshCw, Trash2, ExternalLink, Loader2, Pencil, Upload, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const linkFor = (d) => `${window.location.origin}/demo/${d.token}`;
const fmt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const errMsg = (e) => formatApiErrorDetail(e.response?.data?.detail);
const FIELDS = [
  { k: "org_name", label: "Customer name", ph: "e.g. Acme Ltd" },
  { k: "building_name", label: "Building name", ph: "e.g. Acme House" },
  { k: "location_name", label: "Location / floor", ph: "e.g. 3rd Floor Kitchen" },
];

const LogoField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      toast.success("Logo uploaded");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const shown = value;
  return (
    <div>
      <Label>Customer logo</Label>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-10 w-24 shrink-0 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 grid place-items-center overflow-hidden">
          {shown ? <img src={shown} alt="" className="h-full w-full object-contain" data-testid="demo-edit-logo-preview" /> : <span className="text-[10px] text-slate-400">No logo</span>}
        </div>
        <Input data-testid="demo-edit-logo-url" value={value} placeholder="Paste URL or upload — hidden if blank" onChange={(e) => onChange(e.target.value)} />
        <label className="shrink-0">
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="demo-edit-logo-file" />
          <span className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 px-3 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload</span>
        </label>
      </div>
    </div>
  );
};

const EditDialog = ({ demo, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (demo) setForm({ name: demo.name, show_ribbon: demo.show_ribbon !== false, org_logo_url: demo.overrides?.org_logo_url || "", ...Object.fromEntries(FIELDS.map((f) => [f.k, demo.overrides?.[f.k] || ""])) });
  }, [demo]);
  if (!demo || !form) return null;
  const save = async () => {
    setSaving(true);
    try { await api.put(`/demo-kiosks/${demo.id}`, form); toast.success("Demo details saved"); onClose(); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Customise demo</DialogTitle>
          <DialogDescription>Show the prospect's own name, building and logo so it looks like their live kiosk. Anything left blank is hidden on the demo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Demo name (admin list only)</Label><Input data-testid="demo-edit-name" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          {FIELDS.map((f) => (
            <div key={f.k}>
              <Label>{f.label}</Label>
              <Input data-testid={`demo-edit-${f.k}`} className="mt-1" value={form[f.k]} placeholder={`${f.ph} — hidden if blank`} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
            </div>
          ))}
          <LogoField value={form.org_logo_url} onChange={(v) => setForm({ ...form, org_logo_url: v })} />
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2.5">
            <div><p className="text-sm font-medium">Show "Demo" badge</p><p className="text-xs text-slate-400">Turn off for a true-to-life look. Payments stay simulated either way.</p></div>
            <Switch data-testid="demo-edit-show-ribbon" checked={form.show_ribbon} onCheckedChange={(v) => setForm({ ...form, show_ribbon: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="demo-edit-save" disabled={saving} onClick={save} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CreateDialog = ({ open, onOpenChange, kiosks, onCreated }) => {
  const [form, setForm] = useState({ source_kiosk_id: "", name: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ source_kiosk_id: "", name: "" }); }, [open]);
  const create = async () => {
    setSaving(true);
    try { await api.post("/demo-kiosks", form); toast.success("Demo kiosk created"); onOpenChange(false); onCreated(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a demo kiosk</DialogTitle>
          <DialogDescription>Takes a snapshot of a live kiosk's products, prices, photos and shelf order. Payments are simulated and nothing affects stock, sales or alerts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Copy from live kiosk</Label>
            <Select value={form.source_kiosk_id} onValueChange={(v) => setForm({ ...form, source_kiosk_id: v })}>
              <SelectTrigger data-testid="demo-create-source" className="mt-1"><SelectValue placeholder="Select kiosk" /></SelectTrigger>
              <SelectContent>{kiosks.map((k) => <SelectItem key={k.id} value={k.id}>{k.name} ({k.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Demo name (shown on the kiosk)</Label>
            <Input data-testid="demo-create-name" className="mt-1" value={form.name} placeholder="e.g. Acme Ltd demo" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="demo-create-save" disabled={!form.source_kiosk_id || saving} onClick={create} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create demo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const VisitsDialog = ({ demo, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!demo) { setRows(null); return; }
    api.get(`/demo-kiosks/${demo.id}/visits`).then((r) => setRows(r.data)).catch((e) => { toast.error(errMsg(e)); setRows([]); });
  }, [demo]);
  if (!demo) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Visit history · {demo.name}</DialogTitle>
          <DialogDescription>{demo.visits || 0} open(s) from {demo.unique_visitors || 0} visitor(s), {demo.simulated_purchases || 0} test purchase(s). Your own previews from this page aren't counted.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800" data-testid="demo-visits-list">
          {!rows && <p className="p-4 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></p>}
          {rows && !rows.length && <p className="p-4 text-sm text-center text-slate-400">No visits yet.</p>}
          {(rows || []).map((v) => (
            <div key={v.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`demo-visit-row-${v.id}`}>
              <span className="flex items-center gap-2">{v.event === "purchase" ? <Badge variant="outline" className="text-emerald-600 border-emerald-200">Test purchase</Badge> : <Badge variant="outline"><Eye className="h-3 w-3 mr-1" /> Opened</Badge>}<span className="text-slate-500">{v.device || "Unknown device"}</span></span>
              <span className="text-xs text-slate-400">{fmt(v.at)}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DemoRow = ({ d, onChanged, onEdit, onVisits }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(linkFor(d)); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — select the link and copy it manually"); }
  };
  const remove = () => { if (window.confirm(`Delete "${d.name}"? The share link will stop working.`)) run(() => api.delete(`/demo-kiosks/${d.id}`), "Demo deleted"); };
  return (
    <TableRow data-testid={`demo-row-${d.id}`}>
      <TableCell>
        <p className="font-medium text-slate-900 dark:text-white" data-testid={`demo-name-${d.id}`}>{d.name}</p>
        <p className="text-xs text-slate-400">From {d.source_kiosk_name} ({d.source_kiosk_code}) · {d.product_count} item(s)</p>
        {(d.overrides?.org_name || d.overrides?.building_name) && <p className="text-xs text-blue-600" data-testid={`demo-shows-as-${d.id}`}>Shows as: {[d.overrides.org_name, d.overrides.building_name, d.overrides.location_name].filter(Boolean).join(" · ")}</p>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <code title={linkFor(d)} className={`text-xs whitespace-nowrap ${d.enabled ? "text-slate-600 dark:text-slate-300" : "text-slate-300 line-through"}`} data-testid={`demo-link-${d.id}`}>…/demo/{d.token}</code>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={copy} data-testid={`demo-copy-${d.id}`}><Copy className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" disabled={!d.enabled} onClick={() => window.open(`${linkFor(d)}?preview=1`, "_blank")} data-testid={`demo-open-${d.id}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
        </div>
      </TableCell>
      <TableCell className="text-xs text-slate-500">{fmt(d.snapshot_at)}</TableCell>
      <TableCell>
        <button onClick={() => onVisits(d)} className="text-left group" data-testid={`demo-visits-${d.id}`}>
          <p className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-blue-600"><span data-testid={`demo-visit-count-${d.id}`}>{d.visits || 0}</span> open{d.visits === 1 ? "" : "s"} <span className="text-xs font-normal text-slate-400">· {d.unique_visitors || 0} visitor{d.unique_visitors === 1 ? "" : "s"} · {d.simulated_purchases || 0} test buy{d.simulated_purchases === 1 ? "" : "s"}</span></p>
          <p className="text-xs text-slate-400" data-testid={`demo-last-visit-${d.id}`}>{d.last_visit_at ? `Last opened ${fmt(d.last_visit_at)}` : "Not opened yet"}</p>
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch checked={d.enabled} disabled={busy} data-testid={`demo-toggle-${d.id}`} onCheckedChange={(v) => run(() => api.put(`/demo-kiosks/${d.id}`, { enabled: v }), v ? "Demo link switched on" : "Demo link switched off")} />
          <Badge variant="outline" className={d.enabled ? "text-emerald-600 border-emerald-200" : "text-slate-400"}>{d.enabled ? "On" : "Off"}</Badge>
        </div>
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button variant="outline" size="sm" className="mr-1" onClick={() => onEdit(d)} data-testid={`demo-edit-${d.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Customise</Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => api.post(`/demo-kiosks/${d.id}/refresh`), "Refreshed from live kiosk")} data-testid={`demo-refresh-${d.id}`}><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh from live</Button>
        <Button variant="ghost" size="sm" className="text-rose-600 ml-1" disabled={busy} onClick={remove} data-testid={`demo-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
      </TableCell>
    </TableRow>
  );
};

export default function DemoKiosks() {
  const [demos, setDemos] = useState(null);
  const [kiosks, setKiosks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [visitsFor, setVisitsFor] = useState(null);
  const load = () => api.get("/demo-kiosks").then((r) => setDemos(r.data)).catch((e) => { toast.error(errMsg(e)); setDemos([]); });
  useEffect(() => {
    load();
    api.get("/kiosks").then((r) => setKiosks(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Demo Kiosks</h1>
          <p className="text-slate-500 mt-1">Shareable kiosk demos for prospective customers. Payments are simulated; stock, sales and alerts are never affected.</p>
        </div>
        <Button data-testid="demo-create-button" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Create demo</Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2"><Presentation className="h-4 w-4 text-blue-600" /><span className="font-semibold text-slate-900 dark:text-white">Demos</span></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Demo</TableHead><TableHead>Share link</TableHead><TableHead>Snapshot taken</TableHead><TableHead>Visits</TableHead><TableHead>Link</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(demos || []).map((d) => <DemoRow key={d.id} d={d} onChanged={load} onEdit={setEditing} onVisits={setVisitsFor} />)}
              {demos && !demos.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10" data-testid="demo-empty">No demo kiosks yet — create one from a live kiosk.</TableCell></TableRow>}
              {!demos && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
      <CreateDialog open={open} onOpenChange={setOpen} kiosks={kiosks} onCreated={load} />
      <EditDialog demo={editing} onClose={() => setEditing(null)} onSaved={load} />
      <VisitsDialog demo={visitsFor} onClose={() => setVisitsFor(null)} />
    </div>
  );
}
